import { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useSpring, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, File as FileIcon, UploadCloud, X, Zap } from "lucide-react";
import RobotMascot from "../components/RobotMascot";
import { IMAGES } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_OK = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
const fmt = (v) => "$" + Math.round(v).toLocaleString("en-US");

const Phase = ({ children }) => (
  <AnimatePresence mode="wait">
    <motion.div key="phase" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  </AnimatePresence>
);

const FileRow = ({ file, onRemove, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between rounded-xl border border-black/8 bg-white px-3 py-2.5">
    <span className="flex items-center gap-2 min-w-0">
      <FileIcon className="w-4 h-4 text-[#007AFF] shrink-0" />
      <span className="truncate text-xs text-[#0A1628]">{file.name}</span>
    </span>
    <button type="button" onClick={onRemove} className="rounded-full p-1 text-[#52525B] hover:bg-red-50 hover:text-red-500 transition-colors" aria-label="Remove file">
      <X className="w-3.5 h-3.5" />
    </button>
  </div>
);

const DocSlot = ({ id, label, required, hint, multiple, files, onAdd, onRemove, testid }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const list = multiple ? files : files ? [files] : [];
  return (
    <div className="flex flex-col rounded-2xl border border-black/8 bg-[#F6F9F8] p-4">
      <p className="label-mono text-[#52525B] mb-2">{label}{required && " *"}</p>
      <div
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); onAdd([...e.dataTransfer.files]); }}
        className={`cursor-pointer rounded-xl border-2 border-dashed px-3 py-4 text-center transition-all duration-300 ${
          drag ? "border-[#007AFF] bg-[#007AFF]/5" : "border-black/15 bg-white hover:border-[#007AFF]/50"
        }`}
      >
        <input
          data-testid={`${testid}-input`}
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept=".pdf,.jpg,.jpeg,.png,.heic"
          className="hidden"
          onChange={(e) => { onAdd([...e.target.files]); e.target.value = ""; }}
        />
        <UploadCloud className="w-5 h-5 mx-auto text-[#52525B]" />
        <p className="mono text-[10px] mt-1.5 text-[#52525B]">{hint}</p>
      </div>
      <div className="mt-2 space-y-2">
        {list.map((f, i) => (
          <FileRow key={i} file={f} testid={`${testid}-file-${i}`} onRemove={() => onRemove(i)} />
        ))}
      </div>
    </div>
  );
};

const BridgePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const carried = !!location.state?.prefill;
  const [form, setForm] = useState(() => ({
    business_name: location.state?.prefill?.business_name || "",
    email: location.state?.prefill?.email || "",
    phone: location.state?.prefill?.phone || "",
    time_in_business: "",
  }));
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState("form");
  const [analyzeStep, setAnalyzeStep] = useState(0);
  const [offer, setOffer] = useState(null);
  const [taken, setTaken] = useState(0);
  const [docs, setDocs] = useState({ license: null, check: null, ar: null, cc: [] });
  const [signature, setSignature] = useState("");
  const [agree, setAgree] = useState(false);
  const [signErr, setSignErr] = useState("");
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [zoneErr, setZoneErr] = useState("");

  const spring = useSpring(0, { stiffness: 110, damping: 22 });
  const animated = useTransform(spring, (v) => fmt(v));
  useMemo(() => { spring.set(taken || 0); }, [taken, spring]);

  // 1% per day, 50 days — total repayment 1.5x, paid daily ($150/day per $5,000)
  const payback = Math.round(taken * 1.5);
  const daily = Math.round(taken * 0.03);
  const fee = payback - taken;

  const add = (list) => {
    const ok = [];
    const problems = [];
    for (const f of list) {
      if (!/\.(pdf|jpe?g|png|heic|heif)$/i.test(f.name)) problems.push(`${f.name}: unsupported type`);
      else if (f.size > 15 * 1024 * 1024) problems.push(`${f.name}: over 15MB`);
      else ok.push(f);
    }
    setFiles([...files, ...ok].slice(0, 4));
    setZoneErr(list.length && !ok.length ? problems.join(" · ") : problems.join(" · "));
  };

  const validFile = (f) => /\.(pdf|jpe?g|png|heic|heif)$/i.test(f.name) && f.size <= 15 * 1024 * 1024;

  const start = async () => {
    const e = {};
    if (!form.business_name.trim()) e.business_name = "Required";
    if (!EMAIL_OK(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Required";
    if (files.length < 1) e.files = "Upload at least one statement — 4 months unlocks the full $5,000";
    setErrors(e);
    if (Object.keys(e).length) return;

    setPhase("analyzing");
    const phases = ["Reading your statements…", "Scoring cash flow…", "Structuring your offer…"];
    phases.forEach((p, i) => setTimeout(() => setAnalyzeStep(i + 1), 900 + i * 900));
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(form));
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/bridge`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setTimeout(() => {
        setOffer(data);
        setTaken(data.approved_amount);
        setPhase("approved");
        window.scrollTo(0, 0);
      }, 3400);
    } catch (err) {
      setPhase("form");
      setSubmitError("The robot dropped the connection — please try again.");
    }
  };

  const sign = async () => {
    if (!docs.license || !docs.check) {
      setSignErr("Driver's license and voided check are required to sign.");
      return;
    }
    if (!agree) {
      setSignErr("Please agree to the contract terms.");
      return;
    }
    if (!signature.trim()) {
      setSignErr("Type your full name to sign.");
      return;
    }
    setSignErr("");
    try {
      const ref8 = offer.reference.replace(/^BR-/, "").toLowerCase();
      const dfd = new FormData();
      dfd.append("license", docs.license);
      dfd.append("voided_check", docs.check);
      if (docs.ar) dfd.append("ar", docs.ar);
      docs.cc.forEach((f) => dfd.append("cc_statements", f));
      const dres = await fetch(`${API}/bridge/${ref8}/docs`, { method: "POST", body: dfd });
      if (!dres.ok) throw new Error("docs failed");

      const res = await fetch(`${API}/bridge/${ref8}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setResult(data);
      setPhase("done");
      window.scrollTo(0, 0);
    } catch (err) {
      setSignErr("The robot dropped the connection — please try again.");
    }
  };

  return (
    <div className="relative min-h-screen pb-24 overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade" aria-hidden="true" />
      <div className="absolute top-0 right-[-15%] w-[36rem] h-[36rem] rounded-full bg-[#14D8A5]/20 blur-[130px]" aria-hidden="true" />
      <div className="absolute bottom-0 left-[-15%] w-[32rem] h-[32rem] rounded-full bg-[#00E5FF]/15 blur-[130px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-3xl px-5 md:px-8 pt-24 md:pt-28">
        <button
          data-testid="bridge-exit"
          onClick={() => navigate("/")}
          className="group inline-flex items-center gap-2 text-sm font-medium text-[#52525B] hover:text-[#0A1628] transition-colors"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> lendrobot.com
        </button>

        <div className="mt-6 flex items-start justify-between gap-6">
          <div>
            <p className="label-mono text-[#14D8A5] mb-3">BRIDGE ADVANCE · UP TO $5,000</p>
            <h1 className="font-display font-bold tracking-[-0.02em] leading-[1.02] text-[#0A1628] text-3xl md:text-5xl">
              Cash gap? Bridged<br />in minutes.
            </h1>
            <p className="mt-4 max-w-md text-sm md:text-base leading-relaxed text-[#52525B]">
              Upload your last 4 months of business bank statements. Robo-B7 auto-approves an
              amount on the spot, the contract signs instantly, and funds move the same business day.
            </p>
          </div>
          <span className="lr-float-slow hidden sm:block shrink-0 mt-2">
            <RobotMascot className="w-16" />
          </span>
        </div>

        {carried && (
          <div data-testid="bridge-prefill-banner" className="mt-6 rounded-2xl border border-[#007AFF]/25 bg-[#007AFF]/10 px-5 py-4">
            <p className="text-sm leading-relaxed text-[#0A1628]">
              <Check className="inline w-4 h-4 text-[#0E7C5E] mr-1.5 -mt-0.5" />
              Details carried over from your application — Robo-B7 just needs your statements to
              finish the approval.
            </p>
          </div>
        )}

        {submitError && (
          <p data-testid="bridge-submit-error" className="mt-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-xs font-medium text-red-600">
            {submitError}
          </p>
        )}

        <Phase>
          {phase === "form" && (
            <form
              onSubmit={(e) => { e.preventDefault(); start(); }}
              data-testid="bridge-form"
              className="relative mt-10 rounded-[2rem] border border-black/5 bg-white/85 backdrop-blur-xl p-6 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.05)] space-y-7"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex flex-col">
                  <label className="label-mono text-[#52525B] mb-2">Business legal name *</label>
                  <input data-testid="bridge-biz-name" value={form.business_name} onChange={set("business_name")}
                    placeholder="Acme Robotics LLC"
                    className={`w-full rounded-xl border bg-white px-4 py-3.5 text-sm text-[#0A1628] placeholder-black/30 outline-none transition-all duration-200 ${errors.business_name ? "border-red-400" : "border-black/10 focus:border-[#007AFF] focus:shadow-[0_0_0_4px_rgba(0,122,255,0.12)]"}`} />
                  {errors.business_name && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.business_name}</p>}
                </div>
                <div className="flex flex-col">
                  <label className="label-mono text-[#52525B] mb-2">Time in business</label>
                  <select data-testid="bridge-time" value={form.time_in_business} onChange={set("time_in_business")}
                    className={`w-full rounded-xl border bg-white px-4 py-3.5 text-sm outline-none appearance-none ${form.time_in_business ? "text-[#0A1628] border-black/10" : "text-black/30 border-black/10"} focus:border-[#007AFF]`}>
                    <option value="">Select…</option>
                    {["Under 1 year", "1 – 2 years", "2 – 5 years", "5+ years"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex flex-col">
                  <label className="label-mono text-[#52525B] mb-2">Email *</label>
                  <input data-testid="bridge-email" type="email" value={form.email} onChange={set("email")}
                    placeholder="you@acmerobotics.com"
                    className={`w-full rounded-xl border bg-white px-4 py-3.5 text-sm text-[#0A1628] placeholder-black/30 outline-none transition-all duration-200 ${errors.email ? "border-red-400" : "border-black/10 focus:border-[#007AFF] focus:shadow-[0_0_0_4px_rgba(0,122,255,0.12)]"}`} />
                  {errors.email && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.email}</p>}
                </div>
                <div className="flex flex-col">
                  <label className="label-mono text-[#52525B] mb-2">Mobile phone *</label>
                  <input data-testid="bridge-phone" type="tel" value={form.phone} onChange={set("phone")}
                    placeholder="(555) 000-1234"
                    className={`w-full rounded-xl border bg-white px-4 py-3.5 text-sm text-[#0A1628] placeholder-black/30 outline-none transition-all duration-200 ${errors.phone ? "border-red-400" : "border-black/10 focus:border-[#007AFF] focus:shadow-[0_0_0_4px_rgba(0,122,255,0.12)]"}`} />
                  {errors.phone && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.phone}</p>}
                </div>
              </div>

              <div className="flex flex-col">
                <label className="label-mono text-[#52525B] mb-2">Last 4 months of bank statements *</label>
                <div
                  data-testid="bridge-upload-zone"
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={(e) => { e.preventDefault(); setDrag(false); add([...e.dataTransfer.files]); }}
                  className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all duration-300 ${
                    drag ? "border-[#007AFF] bg-[#007AFF]/5" : "border-black/15 bg-[#F6F9F8] hover:border-[#007AFF]/50"
                  }`}
                >
                  <input data-testid="bridge-upload-input" ref={inputRef} type="file" multiple
                    accept=".pdf,.jpg,.jpeg,.png,.heic" className="hidden"
                    onChange={(e) => { add([...e.target.files]); e.target.value = ""; }} />
                  <UploadCloud className={`w-9 h-9 mx-auto ${drag ? "text-[#007AFF]" : "text-[#52525B]"}`} />
                  <p className="mt-3 font-display text-base font-semibold text-[#0A1628]">Drop your statements here</p>
                  <p className="label-mono mt-2 text-[#52525B]">PDF, JPG, PNG, HEIC · up to 15MB per file · {files.length}/4 added</p>
                </div>
                {zoneErr && <p data-testid="bridge-upload-error" className="mt-1.5 text-xs font-medium text-red-500">{zoneErr}</p>}
                {errors.files && <p className="mt-1.5 text-xs font-medium text-red-500">{errors.files}</p>}
                {files.map((f, i) => (
                  <div key={i} data-testid={`bridge-file-${i}`} className="mt-2 flex items-center justify-between rounded-xl border border-black/8 bg-white px-4 py-2.5">
                    <span className="flex items-center gap-2 min-w-0">
                      <FileIcon className="w-4 h-4 text-[#007AFF] shrink-0" />
                      <span className="truncate text-sm text-[#0A1628]">{f.name}</span>
                    </span>
                    <button type="button" data-testid={`bridge-remove-file-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      className="rounded-full p-1.5 text-[#52525B] hover:bg-red-50 hover:text-red-500 transition-colors" aria-label="Remove file">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <p className="mono mt-2 text-[11px] text-[#52525B]">Each clean month unlocks $1,250 — four clean months unlock the full $5,000.</p>
              </div>

              <button
                type="submit"
                data-testid="bridge-start-btn"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-full bg-[#007AFF] px-9 py-4 text-base font-semibold text-white shadow-[0_12px_32px_rgba(0,122,255,0.35)] transition-all duration-300 hover:bg-[#0A1628] hover:-translate-y-0.5"
              >
                Approve me now <Zap className="w-5 h-5 transition-transform group-hover:scale-125" />
              </button>
            </form>
          )}

          {phase === "analyzing" && (
            <div data-testid="bridge-analyzing" className="mt-10 rounded-[2rem] border border-black/5 bg-white/85 backdrop-blur-xl p-12 text-center shadow-[0_8px_32px_rgba(0,0,0,0.05)]">
              <span className="lr-float inline-block"><RobotMascot className="w-24" /></span>
              <div className="mt-6 space-y-2">
                {["Reading your statements…", "Scoring cash flow…", "Structuring your offer…"].map((t, i) => (
                  <p key={i} className={`mono text-sm transition-all duration-500 ${analyzeStep > i ? "text-[#0A1628]" : "text-black/25"}`}>
                    {analyzeStep > i + 1 ? "✓ " : ""}{t}
                  </p>
                ))}
              </div>
            </div>
          )}

          {phase === "approved" && offer && (
            <div data-testid="bridge-approved" className="mt-10 space-y-6">
              <div className="rounded-[2rem] border border-black/5 bg-[#0B1B33] p-8 md:p-12 text-white shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
                <div className="flex items-center justify-between">
                  <p className="label-mono text-[#7FE9FF]">Auto-approved</p>
                  <span data-testid="bridge-reference" className="mono text-xs text-white/60">{offer.reference}</span>
                </div>
                <motion.p data-testid="bridge-approved-amount" className="font-display text-6xl md:text-7xl font-black mt-4 tracking-tight">
                  {animated}
                </motion.p>
                <p className="mt-3 text-sm text-white/60">
                  Approved from {offer.statements_read} statement{offer.statements_read > 1 ? "s" : ""} · take less if you want
                </p>
                <input
                  data-testid="bridge-take-slider"
                  type="range"
                  min={500}
                  max={offer.approved_amount}
                  step={100}
                  value={taken}
                  onChange={(e) => setTaken(Number(e.target.value))}
                  className="lr-range mt-6"
                  style={{ "--fill": `${((taken - 500) / Math.max(offer.approved_amount - 500, 1)) * 100}%` }}
                  aria-label="Take amount"
                />
                <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  <span className="text-white/60 mono">{fmt(daily)}/day</span>
                  <span className="text-white/60 mono">50 days</span>
                  <span className="text-white/60 mono">1% per day fee</span>
                </div>
              </div>

              <div className="rounded-[2rem] border border-black/5 bg-white/85 backdrop-blur-xl p-6 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.05)]">
                <p className="label-mono text-[#007AFF] mb-4">Verification documents</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <DocSlot
                    testid="bridge-docs-license" label="Driver's license" required
                    hint="PDF, JPG, PNG · tap or drop" multiple={false}
                    files={docs.license}
                    onAdd={(list) => { const f = list.find(validFile); if (list.length && !f) setSignErr("Unsupported file type or size over 15MB"); setDocs((d) => ({ ...d, license: f || d.license })); }}
                    onRemove={() => setDocs((d) => ({ ...d, license: null }))}
                  />
                  <DocSlot
                    testid="bridge-docs-check" label="Voided check" required
                    hint="PDF, JPG, PNG · tap or drop" multiple={false}
                    files={docs.check}
                    onAdd={(list) => { const f = list.find(validFile); if (list.length && !f) setSignErr("Unsupported file type or size over 15MB"); setDocs((d) => ({ ...d, check: f || d.check })); }}
                    onRemove={() => setDocs((d) => ({ ...d, check: null }))}
                  />
                  <DocSlot
                    testid="bridge-docs-ar" label="AR report (if you have it)" hint="Accounts receivable aging · optional"
                    multiple={false} files={docs.ar}
                    onAdd={(list) => { const f = list.find(validFile); if (list.length && !f) setSignErr("Unsupported file type or size over 15MB"); setDocs((d) => ({ ...d, ar: f || d.ar })); }}
                    onRemove={() => setDocs((d) => ({ ...d, ar: null }))}
                  />
                  <DocSlot
                    testid="bridge-docs-cc" label="Card processing statements (if you have them)" hint="Up to 4 · optional"
                    multiple={true} files={docs.cc}
                    onAdd={(list) => setDocs((d) => ({ ...d, cc: [...d.cc, ...list.filter(validFile)].slice(0, 4) }))}
                    onRemove={(i) => setDocs((d) => ({ ...d, cc: d.cc.filter((_, j) => j !== i) }))}
                  />
                </div>

                <p className="label-mono text-[#007AFF] mt-10 mb-4">Your instant contract</p>
                <div data-testid="bridge-contract" className="lr-scroll rounded-2xl border border-black/8 bg-[#F6F9F8] p-6 max-h-64 overflow-y-auto text-xs leading-relaxed text-[#52525B]" data-lenis-prevent>
                  <p><strong>Bridge Advance Agreement — {offer.reference}</strong></p>
                  <p className="mt-3">
                    LendRobot Systems agrees to advance <strong>{fmt(taken)}</strong> to the
                    borrowing business via same-day ACH upon execution of this agreement. In
                    exchange, the business authorizes LendRobot to collect a flat{" "}
                    <strong>1% per day for 50 days</strong>, for total repayment of{" "}
                    <strong>{fmt(payback)}</strong> ({fmt(taken)} principal + {fmt(fee)} fee), paid
                    as <strong>{fmt(daily)} per day for 50 days</strong> via automated daily ACH
                    beginning within 5 business days of funding.
                  </p>
                  <p className="mt-3">
                    Borrower has submitted identity and banking verification: driver's license and
                    voided check{docs.ar ? ", accounts receivable report" : ""}{docs.cc.length ? `, and ${docs.cc.length} credit card processing statement${docs.cc.length > 1 ? "s" : ""}` : ""}.
                  </p>
                  <p className="mt-3">
                    This is a sale of future receivables, not a loan. No collateral is pledged.
                    Early payoff may be completed at any time without penalty. Information in
                    this agreement is confidential between the parties. This demo agreement is
                    illustrative and not a binding offer.
                  </p>
                </div>
                <div className="mt-6 space-y-4">
                  <label className="flex items-start gap-3 text-sm text-[#0A1628] cursor-pointer">
                    <input data-testid="bridge-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                      className="mt-0.5 accent-[#007AFF]" style={{ width: 18, height: 18 }} />
                    <span>I agree to the bridge advance terms above. <span className="text-red-500">*</span></span>
                  </label>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="flex flex-col">
                      <label className="label-mono text-[#52525B] mb-2">Signature (type your full name) *</label>
                      <input data-testid="bridge-signature" value={signature} onChange={(e) => setSignature(e.target.value)}
                        placeholder="Ada Lovelace"
                        className={`w-full rounded-xl border bg-white px-4 py-3.5 text-sm text-[#0A1628] placeholder-black/30 outline-none transition-all duration-200 ${signErr ? "border-red-400" : "border-black/10 focus:border-[#007AFF] focus:shadow-[0_0_0_4px_rgba(0,122,255,0.12)]"}`} />
                    </div>
                    <div className="flex flex-col">
                      <label className="label-mono text-[#52525B] mb-2">Date</label>
                      <div className="rounded-xl border border-black/10 bg-[#F6F9F8] px-4 py-3.5 text-sm text-[#52525B] mono">
                        {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                      </div>
                    </div>
                  </div>
                  {signErr && <p data-testid="bridge-sign-error" className="text-xs font-medium text-red-500">{signErr}</p>}
                </div>
                <button
                  type="button"
                  data-testid="bridge-sign-btn"
                  onClick={sign}
                  className="group mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-full bg-[#14D8A5] px-9 py-4 text-base font-semibold text-[#0A1628] shadow-[0_12px_32px_rgba(20,216,165,0.35)] transition-all duration-300 hover:bg-[#0A1628] hover:text-white hover:-translate-y-0.5"
                >
                  Sign & get funded <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          )}

          {phase === "done" && result && (
            <div data-testid="bridge-done" className="mt-10 rounded-[2rem] border border-black/5 bg-white p-10 text-center shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
              <img src={IMAGES.thumbs} alt="Robot giving a thumbs up" className="w-40 mx-auto" draggable="false" />
              <h2 className="font-display text-3xl font-bold text-[#0A1628] mt-4">Contracted. Funds moving.</h2>
              <p data-testid="bridge-done-reference" className="mono mt-5 inline-block rounded-full bg-[#007AFF]/10 text-[#007AFF] px-5 py-2 text-sm font-semibold">
                {result.reference} · {fmt(result.amount)}
              </p>
              <p className="mt-5 text-sm leading-relaxed text-[#52525B]">
                Contract executed instantly. {fmt(result.amount)} is on its way via same-day ACH —
                {result.funded_eta}. Repay {fmt(Math.round(result.amount * 1.5))} over 50 days via
                daily ACH of {fmt(Math.round(result.amount * 0.03))} per day.
              </p>
              <ul className="mt-7 space-y-3 text-left">
                {["Contract signed — no paperwork", "Same-business-day ACH transfer", "No collateral, no hard credit pull"].map((t, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-[#0A1628]">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#14D8A5]/20 text-[#14D8A5]">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
              <button
                data-testid="bridge-done-back"
                onClick={() => navigate("/")}
                className="mt-9 w-full rounded-full bg-[#0A1628] py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-[#007AFF]"
              >
                Back to lendrobot.com
              </button>
            </div>
          )}
        </Phase>
      </div>
    </div>
  );
};

export default BridgePage;
